import { Nullable, useUrlSearchParams } from '@dai/common'
import { Box, Fade, Paper, Typography } from '@mui/material'
import React, { ReactElement } from 'react'

import { PlanType } from '@/graphql'
import { POSDataSetupStatus } from '@/hooks/use-pos-data-setup-status'
import useT from '@/i18n/useT'

import { useSubscription } from '../../contexts/SubscriptionContext'
import UpgradeMessage, { FeatureMessage } from './UpgradeMessage'

interface FeatureWrapperProps {
  children: ReactElement
  requiredPlan: Nullable<PlanType>
  title?: string
  subtitle?: string
  upgradeMessage?: string | null
  upgradeLink?: string
  minHeight?: number
  posDataSetupStatus: POSDataSetupStatus
  needsPosData?: boolean
}

const Container = ({
  title,
  subtitle,
  children,
  minHeight,
}: {
  title?: string
  subtitle?: string
  children: ReactElement
  minHeight?: number
}) => {
  const TitleHeader = () =>
    title ? (
      <Box
        sx={{
          position: 'absolute',
          bgcolor: 'background.paper',
          borderRadius: 2,
          pl: 4,
          pt: 2,
        }}
      >
        <Typography variant="h6" sx={{ mt: 2 }}>
          {title}
        </Typography>
        {subtitle && (
          <Typography
            variant="body2"
            sx={{
              color: 'text.secondary',
              fontStyle: 'italic',
            }}
          >
            {subtitle}
          </Typography>
        )}
      </Box>
    ) : null

  const contentTopPadding = title ? 16 : 0
  return (
    <Box
      sx={{
        minHeight,
        bgcolor: 'background.paper',
        borderRadius: 2,
        boxShadow: 1,
        position: 'relative',
      }}
    >
      <TitleHeader />
      <Box
        sx={{
          p: 3,
          pt: contentTopPadding,
        }}
      >
        {children}
      </Box>
    </Box>
  )
}

const FeatureWrapper: React.FC<FeatureWrapperProps> = ({
  children,
  requiredPlan,
  title,
  subtitle,
  upgradeMessage,
  upgradeLink = '/pricing',
  minHeight = 280,
  posDataSetupStatus,
  needsPosData = false,
}) => {
  const { t } = useT()
  const {
    handle: { handleNavTo },
  } = useUrlSearchParams()
  const { hasPlanAccess } = useSubscription()

  const hasAccess = requiredPlan ? hasPlanAccess(requiredPlan) : true

  const { hasDataInBigQuery, noLinkedStores, numUnlinkedStores } =
    posDataSetupStatus

  if (!hasAccess && requiredPlan) {
    return (
      <Container title={title} subtitle={subtitle} minHeight={minHeight}>
        <UpgradeMessage
          message={upgradeMessage}
          link={upgradeLink}
          requiredPlan={requiredPlan}
        />
      </Container>
    )
  }

  if (needsPosData && !hasDataInBigQuery) {
    return (
      <Container title={title} subtitle={subtitle} minHeight={minHeight}>
        <FeatureMessage
          message={t('noSalesData.message')}
          onClick={() => handleNavTo('/admin/sales-data')}
          buttonText={t('noSalesData.button')}
        />
      </Container>
    )
  }

  if (needsPosData && noLinkedStores) {
    return (
      <Container title={title} subtitle={subtitle} minHeight={minHeight}>
        <FeatureMessage
          message={t('unlinkedStores.message', { numUnlinkedStores })}
          onClick={() => handleNavTo('/admin/sales-data')}
          buttonText={t('unlinkedStores.button')}
        />
      </Container>
    )
  }

  return (
    <Fade in>
      <Paper sx={{ flex: 1 }}>{children}</Paper>
    </Fade>
  )
}

export default FeatureWrapper
